import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, Spinner, Alert, Modal, Tab, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFolderOpen, 
  faCloudUploadAlt, 
  faSearch, 
  faFileAlt, 
  faFilter,
  faBrain,
  faStar,
  faDownload,
  faTrash,
  faEye,
  faChartBar,
  faLightbulb,
  faRocket,
  faClock,
  faUsers,
  faCheckCircle,
  faExclamationTriangle,
  faNetworkWired,
  faSignature,
  faEdit,
  faExternalLinkAlt,
  faFileContract,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { useDropzone } from 'react-dropzone';
import axiosInstance from 'helpers/api';
import llmAxiosInstance from 'helpers/llmApi';
import toast from 'react-hot-toast';
import WorkflowIntelligenceTab from './WorkflowIntelligenceTab';
import DocumentFieldMapper from '../agent-portal/DocumentFieldMapper';
import MakeFillableModal from './MakeFillableModal';
import ContractBuilder from '../agent-portal/ContractBuilder';
import './BrokerDocumentManager.css';

interface BrokerDocumentManagerProps {
  brokerageId?: string;
  brokerageName?: string;
}

interface Document {
  id: string;
  user_id: string;
  filename: string;
  original_filename: string;
  document_type: string;
  file_size: number;
  file_size_formatted: string;
  upload_date: string;
  processed_at?: string;
  status: string;
  processing_status: string;
  ai_analysis?: any;
  metadata_tags?: any;
  search_vector?: any;
  s3_url: string;
  s3_key: string;
  visibility?: string;
  owner_type?: string;
  brokerage_id?: string;
  description?: string;
  usage_stats?: {
    views: number;
  };
  has_field_mappings?: boolean;
  field_mapping_count?: number;
  anvil_template_id?: string;
  pdffiller_template_id?: string | null;
  pdffiller_fillable_id?: string | null;
  pdffiller_edit_url?: string | null;
  pdffiller_template_uuid?: string | null;
  is_fillable_template?: number | boolean;
}

interface DocumentCategory {
  key: string;
  label: string;
  icon: any;
  count: number;
  color: string;
}

interface PdffillerTemplateOption {
  id: string;
  name?: string |
    null;
  filename: string;
  document_type?: string;
  pdffiller_template_id: string | null;
  pdffiller_fillable_id: string | null;
  pdffiller_edit_url: string | null;
  visibility_scope?: string | null;
  association_id?: number | null;
  brokerage_id?: string | null;
  description?: string | null;
  automation_config?: any;
}

const BrokerDocumentManager: React.FC<BrokerDocumentManagerProps> = ({ 
  brokerageId, 
  brokerageName 
}) => {
  // For testing, use a default brokerage ID if none is provided
  const effectiveBrokerageId = brokerageId || 'connect_realty_brokerage_001';
  console.log('📄 BrokerDocumentManager initialized with:', { 
    brokerageId, 
    effectiveBrokerageId,
    brokerageIdType: typeof brokerageId,
    isUndefined: brokerageId === undefined,
    isNull: brokerageId === null,
    isEmpty: brokerageId === ''
  });
  
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<string>('all'); // Track category for uploads
  const [showPreemptiveMessage, setShowPreemptiveMessage] = useState(false);
  const [preemptiveMessage, setPreemptiveMessage] = useState<string>('');
  const [showFieldMapper, setShowFieldMapper] = useState(false);
  const [fieldMappingDocument, setFieldMappingDocument] = useState<Document | null>(null);
  const [showFillableModal, setShowFillableModal] = useState(false);
  const [fillableDocument, setFillableDocument] = useState<Document | null>(null);
  const [showContractBuilder, setShowContractBuilder] = useState(false);
  const [pdffillerTemplateIdInput, setPdffillerTemplateIdInput] = useState('');
  const [pdffillerFillableIdInput, setPdffillerFillableIdInput] = useState('');
  const [pdffillerEditUrlInput, setPdffillerEditUrlInput] = useState('');
  const [savingPdffiller, setSavingPdffiller] = useState(false);
  const [pdffillerTemplates, setPdffillerTemplates] = useState<PdffillerTemplateOption[]>([]);
  const [loadingPdffillerTemplates, setLoadingPdffillerTemplates] = useState(false);
  const [selectedPdffillerTemplateId, setSelectedPdffillerTemplateId] = useState('__custom__');

  const categories: DocumentCategory[] = [
    { key: 'all', label: 'All Documents', icon: faFolderOpen, count: 0, color: '#00d4ff' },
    { key: 'contract_template', label: 'Contract Templates', icon: faFileAlt, count: 0, color: '#0891b2' },
    { key: 'marketing_material', label: 'Marketing Materials', icon: faRocket, count: 0, color: '#8b5cf6' },
    { key: 'training_resource', label: 'Training Resources', icon: faLightbulb, count: 0, color: '#10b981' },
    { key: 'legal_document', label: 'Legal Documents', icon: faFileAlt, count: 0, color: '#f59e0b' },
    { key: 'form_template', label: 'Forms & Checklists', icon: faCheckCircle, count: 0, color: '#ef4444' }
  ];

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!brokerageId) {
      toast.error('Brokerage ID not available. Please try again.');
      console.error('BrokerDocumentManager: brokerageId is undefined');
      return;
    }

    setUploading(true);
    const uploadPromises = acceptedFiles.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      // Fix: Use the uploadCategory that was set when the upload button was clicked
      const documentType = uploadCategory !== 'all' ? uploadCategory : 'contract_template';
      formData.append('document_type', documentType);
      formData.append('description', `${brokerageName || 'Brokerage'} shared document`);
      formData.append('owner_type', 'brokerage');
      formData.append('visibility', 'brokerage_wide');
      formData.append('brokerage_id', effectiveBrokerageId);
      
      console.log('📤 Uploading document with type:', documentType, 'Upload Category:', uploadCategory, 'Selected Category:', selectedCategory);

      try {
        const response = await axiosInstance.post('/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });

        if (response.data.success) {
          toast.success(`Uploaded ${file.name} successfully as ${documentType}`);
          console.log('✅ Upload successful:', response.data.document);
          console.log('📍 S3 URL:', response.data.document.s3_url);
          console.log('📍 S3 Key:', response.data.document.s3_key);
          return response.data.document;
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
        return null;
      }
    });

    const uploadedDocs = await Promise.all(uploadPromises);
    const successfulUploads = uploadedDocs.filter(doc => doc !== null);
    
    if (successfulUploads.length > 0) {
      fetchDocuments();
    }
    
    setUploading(false);
    setShowUploadModal(false);
  }, [brokerageId, brokerageName, effectiveBrokerageId, uploadCategory]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/rtf': ['.rtf']
    },
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const fetchDocuments = async () => {
    if (!effectiveBrokerageId) {
      console.log('BrokerDocumentManager: Skipping fetch - no brokerageId');
      return;
    }

    try {
      setLoading(true);
      console.log('📄 Fetching documents with params:', {
        owner_type: 'brokerage',
        brokerage_id: effectiveBrokerageId
      });
      
      // Use the existing my-documents endpoint with brokerage filter
      const response = await axiosInstance.get('/documents/my-documents', {
        params: {
          owner_type: 'brokerage',
          brokerage_id: effectiveBrokerageId
        }
      });
      
      if (response.data.success) {
        console.log('📄 Found brokerage documents:', response.data.documents.length);
        
        // If no brokerage documents found, also try to fetch user's personal documents
        if (response.data.documents.length === 0) {
          console.log('📄 No brokerage documents found, fetching user documents...');
          
          const userDocsResponse = await axiosInstance.get('/documents/my-documents', {
            params: {
              // No parameters = get user's documents
            }
          });
          
          if (userDocsResponse.data.success) {
            console.log('📄 Found user documents:', userDocsResponse.data.documents.length);
            setDocuments(userDocsResponse.data.documents);
            setFilteredDocuments(userDocsResponse.data.documents);
          }
        } else {
          setDocuments(response.data.documents);
          setFilteredDocuments(response.data.documents);
        }

        if (selectedDocument) {
          const updatedDoc = response.data.documents.find((doc: Document) => doc.id === selectedDocument.id);
          if (updatedDoc) {
            setSelectedDocument(updatedDoc);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchPdffillerTemplates();
    triggerDocMasterAnalysis();
  }, [effectiveBrokerageId]);

  useEffect(() => {
    if (!selectedDocument) {
      setSelectedPdffillerTemplateId('__custom__');
      return;
    }

    const documentTemplateId = selectedDocument.pdffiller_template_id
      ? String(selectedDocument.pdffiller_template_id)
      : '';
    const documentFillableId = selectedDocument.pdffiller_fillable_id
      ? String(selectedDocument.pdffiller_fillable_id)
      : '';

    setPdffillerTemplateIdInput(documentTemplateId);
    setPdffillerFillableIdInput(documentFillableId);
    setPdffillerEditUrlInput(selectedDocument.pdffiller_edit_url || '');

    const matchingTemplate = pdffillerTemplates.find((tpl) => {
      if (selectedDocument.pdffiller_template_uuid) {
        return tpl.id === selectedDocument.pdffiller_template_uuid;
      }
      return tpl.pdffiller_template_id && tpl.pdffiller_template_id === documentTemplateId;
    });

    setSelectedPdffillerTemplateId(matchingTemplate ? matchingTemplate.id : '__custom__');
  }, [selectedDocument, pdffillerTemplates]);
  
  const triggerDocMasterAnalysis = async () => {
    try {
      console.log('🤖 Triggering DocMaster-Sigma analysis...');
      
      // Trigger document analysis
      const analysisResponse = await axiosInstance.post(`/broker-docmaster/analyze/${effectiveBrokerageId}`, {
        forceRefresh: false
      });
      
      if (analysisResponse.data.success && analysisResponse.data.documentsProcessed > 0) {
        console.log(`✅ DocMaster analyzed ${analysisResponse.data.documentsProcessed} documents`);
        
        // Check for preemptive message
        const messageResponse = await axiosInstance.post(`/broker-docmaster/preemptive-message/${effectiveBrokerageId}`);
        
        if (messageResponse.data.success && messageResponse.data.hasUpdate) {
          setPreemptiveMessage(messageResponse.data.message);
          setShowPreemptiveMessage(true);
          
          // Auto-hide after 10 seconds
          setTimeout(() => {
            setShowPreemptiveMessage(false);
          }, 10000);
        }
      }
    } catch (error) {
      console.error('Error triggering DocMaster:', error);
      // Don't show error to user - this is a background process
    }
  };

  useEffect(() => {
    let filtered = documents;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.document_type === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.filename.toLowerCase().includes(query) ||
        doc.ai_analysis?.summary?.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query)
      );
    }

    setFilteredDocuments(filtered);
  }, [documents, selectedCategory, searchQuery]);

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      console.log('🗑️ Attempting to delete document:', documentId);
      const response = await axiosInstance.delete(`/documents/${documentId}`);
      
      if (response.data.success) {
        toast.success('Document deleted successfully');
        // Remove from local state immediately for better UX
        setDocuments(prev => prev.filter(doc => doc.id !== documentId));
        setFilteredDocuments(prev => prev.filter(doc => doc.id !== documentId));
      }
    } catch (error: any) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete document';
      toast.error(errorMessage);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const fetchPdffillerTemplates = async () => {
    try {
      setLoadingPdffillerTemplates(true);
      const response = await axiosInstance.get('/documents/brokerage/pdffiller-templates');
      if (response.data?.success) {
        const templates: PdffillerTemplateOption[] = (response.data.templates || []).map((template: any) => ({
          id: template.id,
          name: template.name ?? template.filename ?? '',
          filename: template.filename ?? template.name ?? '',
          document_type: template.document_type ?? null,
          pdffiller_template_id: template.pdffiller_template_id ? String(template.pdffiller_template_id) : null,
          pdffiller_fillable_id: template.pdffiller_fillable_id ? String(template.pdffiller_fillable_id) : null,
          pdffiller_edit_url: template.pdffiller_edit_url ?? null,
          visibility_scope: template.visibility_scope ?? null,
          association_id: template.association_id ?? null,
          brokerage_id: template.brokerage_id ?? null,
          description: template.description ?? null,
          automation_config: template.automation_config ?? null
        }));
        setPdffillerTemplates(templates);
      }
    } catch (error) {
      console.error('Error fetching PDFFiller templates:', error);
    } finally {
      setLoadingPdffillerTemplates(false);
    }
  };

  const handleSelectPdffillerTemplate = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedPdffillerTemplateId(value);

    if (!value || value === '__custom__') {
      return;
    }

    const template = pdffillerTemplates.find((tpl) => tpl.id === value);
    if (template) {
      setPdffillerTemplateIdInput(template.pdffiller_template_id || '');
      setPdffillerFillableIdInput(template.pdffiller_fillable_id || '');
      setPdffillerEditUrlInput(template.pdffiller_edit_url || '');
    }
  };

  const handleSavePdffillerMetadata = async () => {
    if (!selectedDocument) return;

    try {
      setSavingPdffiller(true);
      const selectedTemplateUuid = selectedPdffillerTemplateId !== '__custom__'
        ? selectedPdffillerTemplateId
        : null;
      await axiosInstance.put(`/documents/${selectedDocument.id}/pdffiller`, {
        templateUuid: selectedTemplateUuid,
        templateId: pdffillerTemplateIdInput.trim() || null,
        fillableId: pdffillerFillableIdInput.trim() || null,
        editUrl: pdffillerEditUrlInput.trim() || null
      });

      toast.success('PDFFiller metadata saved');
      await fetchDocuments();
      await fetchPdffillerTemplates();
    } catch (error: any) {
      console.error('Error saving PDFFiller metadata:', error);
      toast.error(error?.response?.data?.error || 'Failed to save PDFFiller metadata');
    } finally {
      setSavingPdffiller(false);
    }
  };

  const getCategoryColor = (type: string) => {
    const category = categories.find(cat => cat.key === type);
    return category?.color || '#6b7280';
  };

  const getDocumentIcon = (type: string) => {
    const category = categories.find(cat => cat.key === type);
    return category?.icon || faFileAlt;
  };

  // Update category counts
  const categoriesWithCounts = categories.map(cat => ({
    ...cat,
    count: cat.key === 'all' 
      ? documents.length 
      : documents.filter(doc => doc.document_type === cat.key).length
  }));


  return (
    <Container fluid className="broker-document-manager">
      {/* Preemptive Message Alert */}
      {showPreemptiveMessage && preemptiveMessage && (
        <Alert 
          variant="info" 
          dismissible 
          onClose={() => setShowPreemptiveMessage(false)}
          className="mb-4"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 212, 255, 0.05) 100%)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            color: '#ffffff',
            boxShadow: '0 0 20px rgba(0, 212, 255, 0.2)'
          }}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faBrain} className="me-3" style={{ fontSize: '24px', color: '#00d4ff' }} />
            <div>
              <strong>AI Discovery Update</strong>
              <p className="mb-0 mt-1">{preemptiveMessage}</p>
            </div>
          </div>
        </Alert>
      )}

      {/* Header Section */}
      <div className="document-header mb-4" style={{
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        backdropFilter: 'blur(10px)'
      }}>
        <Row className="align-items-center">
          <Col md={6}>
            <div className="d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                width: "48px", 
                height: "48px",
                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.5)'
              }}>
                <FontAwesomeIcon icon={faFolderOpen} className="text-white" size="lg" />
              </div>
              <div>
                <h2 className="mb-0" style={{ color: '#1f2937', fontWeight: 600 }}>
                  Brokerage Document Center
                </h2>
                <p className="mb-0 text-muted">AI-powered document management for {brokerageName || 'your brokerage'}</p>
              </div>
            </div>
          </Col>
          <Col md={6} className="text-end">
            <div className="d-flex align-items-center justify-content-end gap-3">
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                padding: '8px 16px',
                borderRadius: '20px',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                <FontAwesomeIcon icon={faBrain} className="me-2 text-success" />
                <span className="fw-semibold text-success" style={{ fontSize: '14px' }}>
                  AI Processing Active
                </span>
              </div>
              <Button 
                onClick={() => {
                  setUploadCategory(selectedCategory); // Capture current category for upload
                  setShowUploadModal(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0891b2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: 'bold'
                }}
              >
                <FontAwesomeIcon icon={faCloudUploadAlt} className="me-2" />
                Upload Documents
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* Tabs for Documents and Workflows */}
      <Tab.Container defaultActiveKey="documents">
        <Nav variant="tabs" className="mb-4" style={{
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Nav.Item>
            <Nav.Link eventKey="documents" style={{
              border: 'none',
              borderRadius: '8px 8px 0 0',
              background: 'transparent',
              color: '#6b7280',
              fontWeight: '500',
              padding: '12px 24px'
            }}>
              <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
              Documents
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="workflows" style={{
              border: 'none',
              borderRadius: '8px 8px 0 0',
              background: 'transparent',
              color: '#6b7280',
              fontWeight: '500',
              padding: '12px 24px'
            }}>
              <FontAwesomeIcon icon={faNetworkWired} className="me-2" />
              Workflows & Intelligence
              <Badge bg="success" className="ms-2" style={{ fontSize: '10px' }}>NEW</Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="documents">
            {/* Search and Filter Bar */}
            <Row className="mb-4">
        <Col md={8}>
          <Form.Control
            type="text"
            placeholder="Search documents by name, content, or AI-generated tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#1f2937',
              padding: '12px 20px',
              fontSize: '16px'
            }}
          />
        </Col>
        <Col md={4}>
          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#1f2937',
              padding: '12px 20px',
              fontSize: '16px'
            }}
          >
            <option value="all">All Categories</option>
            {categories.slice(1).map(cat => (
              <option key={cat.key} value={cat.key}>{cat.label}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Category Cards */}
      <Row className="mb-4">
        {categoriesWithCounts.map((category) => (
          <Col key={category.key} md={2} className="mb-3">
            <Card 
              className={`category-card border-0 h-100 ${selectedCategory === category.key ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.key)}
              style={{
                background: selectedCategory === category.key 
                  ? `linear-gradient(135deg, ${category.color}20 0%, ${category.color}10 100%)`
                  : 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                cursor: 'pointer',
                border: selectedCategory === category.key 
                  ? `2px solid ${category.color}` 
                  : '2px solid transparent',
                transition: 'all 0.3s ease'
              }}
            >
              <Card.Body className="text-center p-3">
                <FontAwesomeIcon 
                  icon={category.icon} 
                  size="2x" 
                  style={{ color: category.color }} 
                  className="mb-2"
                />
                <h6 className="mb-1" style={{ color: '#1f2937', fontSize: '14px' }}>
                  {category.label}
                </h6>
                <Badge 
                  bg="secondary" 
                  style={{ 
                    background: `${category.color}20`,
                    color: category.color,
                    border: `1px solid ${category.color}40`
                  }}
                >
                  {category.count}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Documents Grid */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: '#00d4ff' }} />
          <p className="mt-3 text-muted">Loading documents...</p>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <Card className="border-0 text-center py-5" style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px'
        }}>
          <Card.Body>
            <FontAwesomeIcon icon={faFolderOpen} size="3x" className="text-muted mb-3" />
            <h5 className="text-muted">No documents found</h5>
            <p className="text-muted">
              {searchQuery || selectedCategory !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Upload your first document to get started'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {filteredDocuments.map((doc) => (
            <Col key={doc.id} md={4} lg={3} className="mb-4">
              <Card 
                className="document-card border-0 h-100"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setSelectedDocument(doc);
                  setShowDocumentModal(true);
                }}
              >
                <Card.Body className="p-4">
                  <div className="d-flex align-items-start justify-content-between mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: "40px", 
                      height: "40px",
                      background: `${getCategoryColor(doc.document_type)}20`,
                      border: `2px solid ${getCategoryColor(doc.document_type)}40`
                    }}>
                      <FontAwesomeIcon 
                        icon={getDocumentIcon(doc.document_type)} 
                        style={{ color: getCategoryColor(doc.document_type) }}
                      />
                    </div>
                    <div className="d-flex gap-1 flex-wrap">
                      {doc.processing_status === 'completed' && (
                        <Badge bg="success" style={{ fontSize: '11px' }}>
                          <FontAwesomeIcon icon={faBrain} className="me-1" />
                          AI Analyzed
                        </Badge>
                      )}
                      {doc.anvil_template_id && (
                        <Badge bg="primary" style={{ fontSize: '11px' }}>
                          <FontAwesomeIcon icon={faFileContract} className="me-1" />
                          Fillable Template
                        </Badge>
                      )}
                      {doc.pdffiller_template_id && (
                        <Badge bg="info" style={{ fontSize: '11px' }}>
                          <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                          PDFFiller Linked
                        </Badge>
                      )}
                      {doc.has_field_mappings && (
                        <Badge bg="info" style={{ fontSize: '11px' }}>
                          <FontAwesomeIcon icon={faSignature} className="me-1" />
                          {doc.field_mapping_count || 0} Fields
                        </Badge>
                      )}
                    </div>
                  </div>

                  <h6 className="mb-2" style={{ color: '#1f2937', fontWeight: 600 }}>
                    {doc.filename}
                  </h6>

                  {doc.ai_analysis?.summary && (
                    <p className="text-muted small mb-3" style={{ 
                      fontSize: '13px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      <FontAwesomeIcon icon={faBrain} className="me-1" style={{ color: '#00d4ff' }} />
                      {doc.ai_analysis.summary}
                    </p>
                  )}

                  <div className="d-flex justify-content-between align-items-center text-muted small">
                    <span>
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      {formatDate(doc.upload_date)}
                    </span>
                    <span>{doc.file_size_formatted || formatFileSize(doc.file_size)}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <Badge bg="secondary" style={{ fontSize: '11px' }}>
                      <FontAwesomeIcon icon={faUsers} className="me-1" />
                      {doc.usage_stats?.views || 0} views
                    </Badge>
                    <div className="d-flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(doc.s3_url, '_blank');
                        }}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDocument(doc.id);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
          </Tab.Pane>

          <Tab.Pane eventKey="workflows">
            <WorkflowIntelligenceTab brokerageId={effectiveBrokerageId} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Upload Modal */}
      <Modal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{ background: 'rgba(255, 255, 255, 0.95)', border: 'none' }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faCloudUploadAlt} className="me-2" style={{ color: '#00d4ff' }} />
            Upload Documents {uploadCategory !== 'all' && `- ${categories.find(c => c.key === uploadCategory)?.label}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'rgba(255, 255, 255, 0.95)' }}>
          <div {...getRootProps()} className="dropzone" style={{
            border: '2px dashed #00d4ff',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            background: isDragActive ? 'rgba(0, 212, 255, 0.05)' : 'rgba(255, 255, 255, 0.5)',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}>
            <input {...getInputProps()} />
            <FontAwesomeIcon 
              icon={faCloudUploadAlt} 
              size="3x" 
              className="mb-3" 
              style={{ color: '#00d4ff' }}
            />
            <h5 className="mb-2">Drop documents here or click to browse</h5>
            <p className="text-muted mb-0">
              Supported formats: PDF, DOC, DOCX, TXT, RTF (Max 50MB)
            </p>
            <p className="text-muted mb-0 mt-2" style={{ fontSize: '12px' }}>
              Documents will be uploaded to: s3://kairos-iq-client/documents/
            </p>
            {uploading && (
              <div className="mt-3">
                <Spinner animation="border" style={{ color: '#00d4ff' }} />
                <p className="mt-2 mb-0">Uploading and processing with AI...</p>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Document Details Modal */}
      <Modal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{ background: 'rgba(255, 255, 255, 0.95)', border: 'none' }}>
          <Modal.Title>Document Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'rgba(255, 255, 255, 0.95)' }}>
          {selectedDocument && (
            <>
              <h5 className="mb-3">
                {selectedDocument.filename}
                {selectedDocument.anvil_template_id && (
                  <Badge bg="success" className="ms-3" style={{ fontSize: '14px' }}>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                    Fillable Template
                  </Badge>
                )}
                {selectedDocument.pdffiller_template_id && (
                  <Badge bg="info" className="ms-2" style={{ fontSize: '14px' }}>
                    <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                    PDFFiller Linked
                  </Badge>
                )}
              </h5>
              
              {/* Document Preview - Anvil Template or PDF */}
              <div style={{ 
                height: '600px', 
                border: '2px solid #dee2e6', 
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '20px',
                position: 'relative'
              }}>
                {selectedDocument.anvil_template_id ? (
                  <>
                    {console.log('🔍 Loading fillable template:', selectedDocument.anvil_template_id)}
                    <iframe
                      src={selectedDocument.s3_url}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        border: 'none' 
                      }}
                      title="Fillable Document Preview"
                    />
                    <div style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      background: 'rgba(40, 167, 69, 0.9)',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      FILLABLE TEMPLATE
                    </div>
                    <div style={{
                      position: 'absolute',
                      bottom: 15,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(255, 255, 255, 0.95)',
                      color: '#333',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <FontAwesomeIcon icon={faFileContract} style={{ color: '#28a745' }} />
                      <span>This document contains fillable fields that will be automatically populated with transaction data</span>
                    </div>
                  </>
                ) : (
                  <iframe
                    src={selectedDocument.s3_url}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      border: 'none' 
                    }}
                    title="PDF Preview"
                  />
                )}
              </div>

              {selectedDocument.ai_analysis?.summary && (
                <Alert variant="info" className="mb-3">
                  <FontAwesomeIcon icon={faBrain} className="me-2" />
                  <strong>AI Summary:</strong> {selectedDocument.ai_analysis.summary}
                </Alert>
              )}

              {selectedDocument.has_field_mappings && (
                <Alert variant="success" className="mb-3">
                  <FontAwesomeIcon icon={faSignature} className="me-2" />
                  <strong>Field Mapping Status:</strong> This document has {selectedDocument.field_mapping_count || 0} mapped fields ready for agent use.
                </Alert>
              )}

              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Type:</strong> {selectedDocument.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                  <p><strong>Size:</strong> {selectedDocument.file_size_formatted || formatFileSize(selectedDocument.file_size)}</p>
                  <p><strong>Uploaded:</strong> {formatDate(selectedDocument.upload_date)}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Views:</strong> {selectedDocument.usage_stats?.views || 0}</p>
                  <p><strong>Processed:</strong> {selectedDocument.processed_at ? formatDate(selectedDocument.processed_at) : 'Not processed'}</p>
                  <p><strong>Status:</strong> <Badge bg={selectedDocument.processing_status === 'completed' ? 'success' : 'warning'}>
                    {selectedDocument.processing_status}
                  </Badge></p>
                </Col>
              </Row>

              <Card className="mb-4">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <strong>PDFFiller Template</strong>
                      <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                        Link this document to an existing PDFFiller template so agents can edit and sign versions downstream.
                      </p>
                    </div>
                    {selectedDocument.pdffiller_template_id ? (
                      <Badge bg="info">Linked</Badge>
                    ) : (
                      <Badge bg="secondary">Not Linked</Badge>
                    )}
                  </div>

                  <Form>
                    <Form.Group className="mb-3" controlId="pdffillerTemplateSelect">
                      <Form.Label>Select Existing Template</Form.Label>
                      <Form.Select
                        value={selectedPdffillerTemplateId}
                        onChange={handleSelectPdffillerTemplate}
                        disabled={loadingPdffillerTemplates || pdffillerTemplates.length === 0}
                      >
                        <option value="__custom__">Manual entry</option>
                        {pdffillerTemplates.map((template) => {
                          const labelParts = [template.name || template.filename || 'Template'];
                          if (template.visibility_scope && template.visibility_scope !== 'global') {
                            labelParts.push(`(${template.visibility_scope})`);
                          }
                          return (
                            <option key={template.id} value={template.id}>
                              {labelParts.join(' ')}
                            </option>
                          );
                        })}
                      </Form.Select>
                      {loadingPdffillerTemplates && (
                        <small className="text-muted">Loading templates…</small>
                      )}
                      {!loadingPdffillerTemplates && pdffillerTemplates.length === 0 && (
                        <small className="text-muted">No PDFFiller templates linked yet. Save one from the Templates tab to reuse it here.</small>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="pdffillerTemplateIdInput">
                      <Form.Label>Template ID</Form.Label>
                      <Form.Control
                        value={pdffillerTemplateIdInput}
                        onChange={(e) => {
                          setSelectedPdffillerTemplateId('__custom__');
                          setPdffillerTemplateIdInput(e.target.value);
                        }}
                        placeholder="e.g. 1967740924"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="pdffillerFillableIdInput">
                      <Form.Label>Fillable Form ID (optional)</Form.Label>
                      <Form.Control
                        value={pdffillerFillableIdInput}
                        onChange={(e) => {
                          setSelectedPdffillerTemplateId('__custom__');
                          setPdffillerFillableIdInput(e.target.value);
                        }}
                        placeholder="If available from PDFFiller"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="pdffillerEditUrlInput">
                      <Form.Label>Edit URL</Form.Label>
                      <Form.Control
                        value={pdffillerEditUrlInput}
                        onChange={(e) => {
                          setSelectedPdffillerTemplateId('__custom__');
                          setPdffillerEditUrlInput(e.target.value);
                        }}
                        placeholder="https://www.pdffiller.com/..."
                      />
                    </Form.Group>

                    <div className="d-flex gap-2">
                      <Button
                        variant="primary"
                        onClick={handleSavePdffillerMetadata}
                        disabled={savingPdffiller}
                      >
                        {savingPdffiller ? 'Saving...' : 'Save Link'}
                      </Button>
                      {pdffillerEditUrlInput && (
                        <Button
                          variant="outline-info"
                          onClick={() => window.open(pdffillerEditUrlInput, '_blank')}
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                          Open PDFFiller
                        </Button>
                      )}
                    </div>
                  </Form>
                </Card.Body>
              </Card>

              {selectedDocument.metadata_tags && Array.isArray(selectedDocument.metadata_tags) && selectedDocument.metadata_tags.length > 0 && (
                <div className="mb-3">
                  <strong>Tags:</strong>
                  <div className="mt-1">
                    {selectedDocument.metadata_tags.map((tag, idx) => (
                      <Badge key={idx} bg="secondary" className="me-1">
                        {tag.trim()}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="d-flex gap-2 mt-4">
                <Button 
                  variant="primary"
                  onClick={() => window.open(selectedDocument.s3_url, '_blank')}
                >
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  View Document
                </Button>
                
                {!selectedDocument.anvil_template_id && (
                  <Button 
                    variant="success"
                    onClick={() => {
                      setFillableDocument(selectedDocument);
                      setShowFillableModal(true);
                      setShowDocumentModal(false);
                    }}
                  >
                    <FontAwesomeIcon icon={faFileContract} className="me-2" />
                    Make Fillable
                  </Button>
                )}
                
                <Button 
                  variant="outline-danger"
                  onClick={() => {
                    handleDeleteDocument(selectedDocument.id);
                    setShowDocumentModal(false);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Delete
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Field Mapping Modal */}
      <Modal
        show={showFieldMapper}
        onHide={() => {
          setShowFieldMapper(false);
          setFieldMappingDocument(null);
        }}
        size="xl"
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faSignature} className="me-2" />
            Field Mapping - {fieldMappingDocument?.filename}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {fieldMappingDocument && (
            <DocumentFieldMapper
              documentUrl={fieldMappingDocument.s3_url}
              documentName={fieldMappingDocument.filename}
              documentId={fieldMappingDocument.id}
              clientData={{
                brokerage_id: effectiveBrokerageId,
                document_type: fieldMappingDocument.document_type
              }}
              onSaveMapping={async (fields) => {
                console.log('Field mapping saved for brokerage template:', fields);
                // The DocumentFieldMapper already handles saving to the API
                // Just close the modal
                setShowFieldMapper(false);
                setFieldMappingDocument(null);
                toast.success('Field mapping saved successfully!');
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Make Fillable Modal */}
      {fillableDocument && (
        <MakeFillableModal
          show={showFillableModal}
          onHide={() => {
            setShowFillableModal(false);
            setFillableDocument(null);
          }}
          documentId={String(fillableDocument.id)}
          documentName={fillableDocument.filename || fillableDocument.original_filename}
          onConversionComplete={() => {
            toast.success('PDFFiller template ready!');
            setShowFillableModal(false);
            setFillableDocument(null);
            setShowDocumentModal(false);
            fetchDocuments(); // Refresh document list
          }}
        />
      )}

      {/* Contract Builder Modal */}
      <ContractBuilder
        show={showContractBuilder}
        onHide={() => setShowContractBuilder(false)}
        clientData={{
          brokerageId: effectiveBrokerageId,
          brokerageName: brokerageName
        }}
        onComplete={(pdfBlob) => {
          toast.success('Contract created and ready for signing via Documenso!');
          setShowContractBuilder(false);
          fetchDocuments(); // Refresh document list
        }}
      />
    </Container>
  );
};

export default BrokerDocumentManager;
